import { useState, useEffect, useCallback } from "react";
import { FEATURED_PRODUCTS } from "./data/featuredProducts";
import { searchNaver, fetchHotDeals } from "./services/naverSearch";
import {
  Home, Search, Heart, Bell, User, Plus, MessageCircle,
  ShoppingCart, TrendingDown, TrendingUp,
  Settings, BarChart2, Package, Gift, Users, BellRing,
  ChevronRight,
} from "lucide-react";
import {
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Area, AreaChart,
} from "recharts";

/* ── 반응형 훅 ── */
function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const fn = () => setWidth(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return width;
}

/* ── 데이터 ── */
const CATEGORIES = [
  { key: "all",         label: "전체",         icon: "✨" },
  { key: "baby",        label: "육아용품",      icon: "🍼" },
  { key: "food",        label: "식품/생필품",   icon: "🛒" },
  { key: "electronics", label: "전자제품",      icon: "💻" },
  { key: "fashion",     label: "패션/의류",     icon: "👗" },
  { key: "beauty",      label: "뷰티",          icon: "💄" },
  { key: "health",      label: "건강",          icon: "💊" },
  { key: "pet",         label: "반려동물",      icon: "🐾" },
  { key: "sports",      label: "스포츠",        icon: "🏋️" },
  { key: "home",        label: "가구/인테리어", icon: "🛋️" },
];

const CAT_COLORS = {
  baby:"#f472b6", food:"#fb923c", electronics:"#60a5fa",
  fashion:"#a78bfa", beauty:"#f43f5e", health:"#34d399",
  pet:"#fbbf24", sports:"#22d3ee", home:"#94a3b8", all:"#c084fc",
};

// 초기값: 가격 로딩 전 skeleton용
const PRODUCTS_INIT = FEATURED_PRODUCTS.map(p => ({
  ...p, price: 0, originalPrice: 0, discountRate: 0,
  drop: 0, high: 0, low: 0, target: 0,
  history: [], watching: 0, store: '', imageUrl: '',
}));

function genNaverHistory(price, originalPrice, seed = 1) {
  let s = seed * 9973;
  const rand = () => { s = (s * 1664525 + 1013904223) & 0xffffffff; return (s >>> 0) / 0xffffffff; };
  return Array.from({length: 8}, (_, i) => {
    if (i === 7) return price;
    const t = (7 - i) / 7;
    const base = price + (originalPrice - price) * t * 0.7;
    return Math.round((base + base * (rand() * 0.06 - 0.03)) / 100) * 100;
  });
}

const HOTDEALS = [
  { id:1, icon:"🩲", name:"하기스 맥스드라이 3단계",    price:31500, original:45000, discount:30, store:"쿠팡",    deadline:3600*2+1800, category:"baby" },
  { id:2, icon:"🥟", name:"비비고 왕교자 1.05kg",       price:6900,  original:11900, discount:42, store:"쿠팡",    deadline:3600*1,      category:"food" },
  { id:3, icon:"🎧", name:"삼성 갤럭시 버즈3 Pro",      price:159000,original:239000,discount:33, store:"11번가",  deadline:3600*5,      category:"electronics" },
  { id:4, icon:"🧴", name:"라네즈 워터슬리핑 마스크",   price:21000, original:35000, discount:40, store:"올리브영",deadline:900,          category:"beauty" },
];

const PRICE_SURGE = [
  { icon:"🥜", name:"피스타치오 1kg",           rise:156, price:49900, prev:19500, category:"food" },
  { icon:"🧻", name:"3M 스카치 테이프 10입",    rise:43,  price:8900,  prev:6200,  category:"food" },
  { icon:"🐟", name:"동원 참치 12캔",           rise:28,  price:18900, prev:14800, category:"food" },
  { icon:"🧴", name:"다우니 섬유유연제 3L",      rise:22,  price:14900, prev:12200, category:"food" },
  { icon:"🍼", name:"앱솔루트 분유 800g",       rise:18,  price:34000, prev:28800, category:"baby" },
];

const PRICE_DROP = [
  { icon:"👟", name:"나이키 에어맥스 270",       drop:-25, price:89000, prev:119000, category:"fashion" },
  { icon:"💊", name:"종근당 오메가3 180캡슐",    drop:-24, price:14900, prev:19900,  category:"health" },
  { icon:"🥟", name:"비비고 왕교자 1.05kg",     drop:-42, price:6900,  prev:11900,  category:"food" },
  { icon:"👕", name:"유니클로 히트텍 극세",      drop:-33, price:14900, prev:22900,  category:"fashion" },
  { icon:"🐾", name:"로얄캐닌 고양이 어덜트",    drop:-17, price:33000, prev:39800,  category:"pet" },
];

const ALERTS = [
  { id:1, icon:"🎉", product:"삼성 갤럭시 버즈3 Pro",    msg:"목표가 도달! 160,000원 이하로 떨어졌어요", time:"방금 전",  category:"electronics" },
  { id:2, icon:"📉", product:"하기스 맥스드라이 3단계",  msg:"가격 하락! 38,900원으로 내렸어요",         time:"1시간 전", category:"baby" },
  { id:3, icon:"🔥", product:"비비고 왕교자",            msg:"주간 최저가 경신!",                        time:"3시간 전", category:"food" },
  { id:4, icon:"⚡", product:"유니클로 히트텍",           msg:"타임세일 시작! 24시간 한정",               time:"5시간 전", category:"fashion" },
];

const PERIODS = [
  { key:"1d",  label:"1일",  days:1  },
  { key:"7d",  label:"7일",  days:7  },
  { key:"30d", label:"30일", days:30 },
  { key:"90d", label:"90일", days:90 },
];

function genMockHistory(naverPrice, seed, days) {
  if (!naverPrice) return [];
  let sN = (seed * 9973) & 0xffffffff;
  let sC = ((seed + 71) * 9973) & 0xffffffff;
  const rN = () => { sN = (sN * 1664525 + 1013904223) & 0xffffffff; return (sN >>> 0) / 0xffffffff; };
  const rC = () => { sC = (sC * 1664525 + 1013904223) & 0xffffffff; return (sC >>> 0) / 0xffffffff; };
  const today = new Date();
  return Array.from({ length: days }, (_, idx) => {
    const date = new Date(today);
    date.setDate(date.getDate() - (days - 1 - idx));
    const t = (days - 1 - idx) / Math.max(days - 1, 1);
    const last = idx === days - 1;
    const nBase = naverPrice * (1 + t * 0.08);
    const cBase = naverPrice * 1.015 * (1 + t * 0.1);
    return {
      date:         date.toISOString().split('T')[0],
      naverPrice:   last ? naverPrice : Math.max(Math.round(nBase * (1 + rN() * 0.06 - 0.03) / 100) * 100, 100),
      coupangPrice: last ? Math.round(naverPrice * 1.015 / 100) * 100 : Math.max(Math.round(cBase * (1 + rC() * 0.06 - 0.03) / 100) * 100, 100),
    };
  });
}

/* ── SVG Mini chart (카드용) ── */
const MiniChart = ({ data, color }) => {
  const max = Math.max(...data), min = Math.min(...data), range = max - min || 1;
  const w = 80, h = 32;
  const pts = data.map((v,i)=>`${(i/(data.length-1))*w},${h-((v-min)/range)*h}`).join(" ");
  const last = pts.split(" ").at(-1).split(",");
  return (
    <svg width={w} height={h} style={{overflow:"visible",flexShrink:0}}>
      <defs>
        <linearGradient id={`g${color.replace("#","")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill={`url(#g${color.replace("#","")})`}/>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx={last[0]} cy={last[1]} r="3" fill={color} stroke="#fff" strokeWidth="1.5"/>
    </svg>
  );
};

/* ── recharts 커스텀 툴팁 ── */
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const naver   = payload.find(p => p.dataKey === "naverPrice");
  const coupang = payload.find(p => p.dataKey === "coupangPrice");
  return (
    <div style={{
      background:"#fff",border:"1.5px solid #e4d9f5",borderRadius:12,
      padding:"8px 12px",fontSize:12,boxShadow:"0 4px 16px rgba(168,85,247,0.12)",minWidth:130,
    }}>
      <div style={{color:"#94a3b8",marginBottom:5,fontSize:11}}>{label}</div>
      {coupang && <div style={{fontWeight:800,color:"#e8003d",marginBottom:2}}>쿠팡 {coupang.value.toLocaleString()}원</div>}
      {naver   && <div style={{fontWeight:800,color:"#03c75a"}}>네이버 {naver.value.toLocaleString()}원</div>}
    </div>
  );
};

/* ══════════════════════════════════════
   앱 메인
══════════════════════════════════════ */
export default function App() {
  const winWidth = useWindowWidth();
  const isPC = winWidth >= 768;

  const [tab, setTab]                 = useState("home");
  const [activeCat, setActiveCat]     = useState("all");
  const [selected, setSelected]       = useState(null);
  const [addModal, setAddModal]       = useState(false);
  const [searchText, setSearchText]   = useState("");
  const [notifyMethod, setNotifyMethod] = useState("kakao");
  const [targetInput, setTargetInput] = useState("");
  const [mounted, setMounted]         = useState(false);
  const [kakaoModal, setKakaoModal]   = useState(false);
  const [hotTab, setHotTab]           = useState("drop");
  const [countdown, setCountdown]     = useState({});
  const [isLoggedIn, setIsLoggedIn]   = useState(false);
  const [loginModal, setLoginModal]   = useState(false);
  const [loginTarget, setLoginTarget] = useState("");
  const [userProfile, setUserProfile] = useState(null);
  const [chartPeriod, setChartPeriod] = useState("7d");
  const [dbHistory,   setDbHistory]   = useState({});
  const [kakaoReady, setKakaoReady]   = useState(false);
  const [naverResults, setNaverResults] = useState([]);
  const [isSearching, setIsSearching]   = useState(false);
  const [searchError, setSearchError]   = useState("");
  // eslint-disable-next-line no-unused-vars
  const [hotDeals, setHotDeals]         = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [hotDealsLoading, setHotDealsLoading] = useState(true);
  const [products, setProducts]         = useState(PRODUCTS_INIT);
  const [productsLoading, setProductsLoading] = useState(true);

  /* 마운트 애니메이션 */
  useEffect(() => { setTimeout(() => setMounted(true), 100); }, []);

  /* 핫딜 카운트다운 */
  useEffect(() => {
    const init = {};
    HOTDEALS.forEach(d => { init[d.id] = d.deadline; });
    setCountdown(init);
    const timer = setInterval(() => {
      setCountdown(prev => {
        const next = {...prev};
        Object.keys(next).forEach(k => { if (next[k] > 0) next[k]--; });
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  /* 네이버 핫딜 fetch */
  useEffect(() => {
    fetchHotDeals()
      .then(items => {
        const withTimer = items.map((item, i) => ({
          ...item,
          deadline: 3600 * (2 + i) + Math.floor(Math.random() * 1800),
          history: genNaverHistory(item.price, item.originalPrice, item.id || i),
          coupangPrice: Math.round(item.price * (1 + 0.03 + Math.random() * 0.05) / 100) * 100,
          naverPrice: item.price,
          target: Math.round(item.price * 0.9 / 100) * 100,
          high: item.originalPrice,
          low: item.price,
          watching: Math.floor(Math.random() * 500) + 50,
          category: "all",
          icon: "📦",
        }));
        setHotDeals(withTimer);
        setCountdown(prev => {
          const next = {...prev};
          withTimer.forEach(d => { next[d.id] = d.deadline; });
          return next;
        });
      })
      .catch(() => {})
      .finally(() => setHotDealsLoading(false));
  }, []);

  /* 카카오 SDK 로드 */
  useEffect(() => {
    if (window.Kakao) { setKakaoReady(true); return; }
    const script = document.createElement("script");
    script.src = "https://t1.kakaocdn.net/kakao_js_sdk/2.7.2/kakao.min.js";
    script.crossOrigin = "anonymous";
    script.onload = () => {
      const key = process.env.REACT_APP_KAKAO_KEY;
      if (key && !window.Kakao.isInitialized()) {
        window.Kakao.init(key);
      }
      setKakaoReady(true);
    };
    document.head.appendChild(script);
  }, []);

  /* 홈 상품 fetch (네이버 실가격) */
  useEffect(() => {
    fetch('/api/featured-products')
      .then(r => r.json())
      .then(data => {
        if (!data.products) return;
        setProducts(data.products.map(p => ({
          ...p,
          drop:    p.discountRate > 0 ? -p.discountRate : 0,
          high:    p.originalPrice || p.price,
          low:     p.price,
          target:  p.price ? Math.round(p.price * 0.9 / 100) * 100 : 0,
          history: p.price ? genNaverHistory(p.price, p.originalPrice || p.price, p.id) : [],
          coupangHistory: p.price ? genNaverHistory(
            Math.round(p.price * 1.015 / 100) * 100,
            Math.round((p.originalPrice || p.price) * 1.02 / 100) * 100,
            p.id + 71
          ) : [],
          store:   p.mallName || '네이버',
          watching: Math.floor((p.id * 137) % 500) + 50,
        })));
      })
      .catch(() => {})
      .finally(() => setProductsLoading(false));
  }, []);

  /* 상품 상세 진입 시 DB 가격 히스토리 fetch */
  useEffect(() => {
    if (!selected) return;
    if (dbHistory[selected.id] !== undefined) return;
    fetch(`/api/price-history?product_id=${selected.id}&days=90`)
      .then(r => r.json())
      .then(({ data }) => setDbHistory(prev => ({ ...prev, [selected.id]: data || [] })))
      .catch(() => setDbHistory(prev => ({ ...prev, [selected.id]: [] })));
  }, [selected?.id]); // eslint-disable-line

  /* 카카오 로그인 */
  const handleKakaoLogin = useCallback(() => {
    if (!kakaoReady || !window.Kakao?.isInitialized()) {
      /* 개발용 fallback: App Key 미설정 시 mock 로그인 */
      setIsLoggedIn(true);
      setUserProfile({ nickname: "절약요정", imageUrl: null });
      setLoginModal(false);
      return;
    }
    window.Kakao.Auth.login({
      scope: "profile_nickname,profile_image",
      success: () => {
        window.Kakao.API.request({
          url: "/v2/user/me",
          success: (res) => {
            const profile = res.kakao_account?.profile;
            setIsLoggedIn(true);
            setUserProfile({
              nickname: profile?.nickname || "절약요정",
              imageUrl: profile?.profile_image_url || null,
            });
            setLoginModal(false);
          },
          fail: () => {
            setIsLoggedIn(true);
            setUserProfile({ nickname: "절약요정", imageUrl: null });
            setLoginModal(false);
          },
        });
      },
      fail: (err) => {
        console.error("Kakao login failed:", err);
        alert("카카오 로그인에 실패했어요. 잠시 후 다시 시도해주세요.");
      },
    });
  }, [kakaoReady]);

  /* 네이버 쇼핑 검색 */
  const handleSearch = useCallback(async (query) => {
    const q = (query || searchText).trim();
    if (!q) return;
    setIsSearching(true);
    setSearchError("");
    setNaverResults([]);
    setTab("search");
    try {
      const items = await searchNaver(q);
      setNaverResults(items);
    } catch (err) {
      setSearchError(err.message || "검색 중 오류가 발생했어요.");
    } finally {
      setIsSearching(false);
    }
  }, [searchText]);

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  /* 유틸 */
  const fmtTime = s => {
    if (s <= 0) return "종료";
    const h = Math.floor(s/3600), m = Math.floor((s%3600)/60), sec = s%60;
    if (h > 0) return `${h}시간 ${m}분`;
    return `${m}분 ${sec}초`;
  };
  const fmt = n => n.toLocaleString() + "원";
  const catColor = key => CAT_COLORS[key] || "#c084fc";
  const catLabel = key => CATEGORIES.find(c=>c.key===key)?.label || key;
  const catIcon  = key => CATEGORIES.find(c=>c.key===key)?.icon  || "📦";

  const filtered = products.filter(p =>
    (activeCat === "all" || p.category === activeCat) &&
    (!searchText || p.name.includes(searchText))
  );
  const topDrops = [...products].filter(p => p.price > 0).sort((a,b) => a.drop - b.drop).slice(0,4);

  /* recharts 데이터 */
  const getChartData = (product) => {
    const periodCfg = PERIODS.find(p => p.key === chartPeriod) || PERIODS[1];
    const db = dbHistory[product.id];

    // DB 데이터가 있으면 실제 데이터 사용
    if (db && db.length > 0) {
      const since = new Date();
      since.setDate(since.getDate() - periodCfg.days);
      const filtered = db.filter(d => new Date(d.recorded_date) >= since);
      if (filtered.length > 0) {
        return filtered.map(d => ({
          name:         d.recorded_date.slice(5).replace('-', '/'),
          naverPrice:   d.naver_price   || 0,
          coupangPrice: d.coupang_price || d.naver_price || 0,
        }));
      }
    }

    // 목 데이터 fallback
    if (!product.price) return [];
    return genMockHistory(product.price, product.id, periodCfg.days).map(d => ({
      name:         d.date.slice(5).replace('-', '/'),
      naverPrice:   d.naverPrice,
      coupangPrice: d.coupangPrice,
    }));
  };

  /* ── 공통 카드 스타일 ── */
  const cardBase = {
    background:"#fff", borderRadius:20, padding:isPC?20:16,
    border:"1.5px solid rgba(192,132,252,0.1)",
    boxShadow:"0 2px 14px rgba(168,85,247,0.06)", cursor:"pointer",
  };

  return (
    <div style={{
      minHeight:"100vh", background:"#faf8ff",
      fontFamily:"'Noto Sans KR','Apple SD Gothic Neo',sans-serif",
      color:"#18181b",
    }}>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes float  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        .fade-up  { animation: fadeUp 0.4s ease forwards; }
        .card-hover { transition:transform 0.18s,box-shadow 0.18s; }
        .card-hover:hover { transform:translateY(-3px); box-shadow:0 12px 32px rgba(192,132,252,0.18)!important; }
        .btn-hover  { transition:all 0.15s; }
        .btn-hover:hover { filter:brightness(1.08); transform:translateY(-1px); }
        ::-webkit-scrollbar { width:4px; height:4px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:#e4d9f5; border-radius:10px; }
        .recharts-tooltip-cursor { stroke: rgba(168,85,247,0.2); }
      `}</style>

      {/* ════ 헤더 ════ */}
      <header style={{
        background:"rgba(255,255,255,0.92)",
        backdropFilter:"blur(24px)", WebkitBackdropFilter:"blur(24px)",
        borderBottom:"1px solid rgba(192,132,252,0.15)",
        padding: isPC ? "0 40px" : "0 20px",
        position:"sticky", top:0, zIndex:200,
        display:"flex", alignItems:"center", justifyContent:"space-between",
        height: isPC ? 64 : 58,
        maxWidth: isPC ? "100%" : "100%",
      }}>
        {/* 로고 */}
        <div style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}} onClick={()=>setTab("home")}>
          <div style={{
            width:36, height:36, borderRadius:12,
            background:"linear-gradient(135deg,#c084fc,#f472b6)",
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:20, boxShadow:"0 4px 12px rgba(192,132,252,0.4)",
            animation:"float 3s ease-in-out infinite",
          }}>🧚</div>
          <div>
            <div style={{
              fontWeight:900, fontSize:17,
              background:"linear-gradient(135deg,#a855f7,#ec4899)",
              WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
              letterSpacing:"-0.5px", lineHeight:1.2,
            }}>절약요정</div>
            <div style={{fontSize:9,color:"#c084fc",fontWeight:600,letterSpacing:"0.5px"}}>SAVE FAIRY ✨</div>
          </div>
        </div>

        {/* PC: 검색창 */}
        {isPC && (
          <div style={{
            flex:1, maxWidth:440, margin:"0 32px",
            background:"#f5f0ff", borderRadius:50, padding:"9px 18px",
            display:"flex", alignItems:"center", gap:10,
            border:"1.5px solid rgba(192,132,252,0.2)",
          }}>
            <Search size={15} color="#c4b5fd"/>
            <input
              placeholder="상품명으로 검색..."
              value={searchText}
              onChange={e=>setSearchText(e.target.value)}
              onFocus={()=>setTab("search")}
              onKeyDown={handleSearchKeyDown}
              style={{border:"none",outline:"none",flex:1,fontSize:13,background:"transparent",color:"#18181b"}}
            />
            {searchText && (
              <button onClick={()=>handleSearch()} style={{background:"none",border:"none",cursor:"pointer",color:"#a855f7",fontSize:12,fontWeight:700,padding:0}}>검색</button>
            )}
          </div>
        )}

        {/* PC: 탭 네비 */}
        {isPC && (
          <nav style={{display:"flex",alignItems:"center",gap:4}}>
            {[
              ["home",      <Home size={16}/>,      "홈"],
              ["search",    <Search size={16}/>,    "탐색"],
              ["watchlist", <Heart size={16}/>,     "관심"],
              ["alerts",    <Bell size={16}/>,      "알림"],
              ["mypage",    <User size={16}/>,      "MY"],
            ].map(([key,icon,label])=>(
              <button key={key} onClick={()=>setTab(key)} style={{
                display:"flex", alignItems:"center", gap:5,
                padding:"7px 14px", border:"none",
                borderRadius:10, cursor:"pointer", fontSize:13, fontWeight:600,
                color: tab===key ? "#a855f7" : "#94a3b8",
                background: tab===key ? "#f5f0ff" : "none",
                transition:"all 0.15s",
              }}>{icon}{label}</button>
            ))}
          </nav>
        )}

        {/* 우측 버튼 */}
        <div style={{display:"flex",gap:8,alignItems:"center",marginLeft: isPC ? 16 : 0}}>
          {!isPC && (
            <button onClick={()=>setKakaoModal(true)} style={{
              display:"flex", alignItems:"center", gap:5,
              background:"#FEE500", color:"#3A1D1D",
              border:"none", borderRadius:20, padding:"7px 14px",
              fontSize:11, fontWeight:800, cursor:"pointer",
              boxShadow:"0 2px 8px rgba(254,229,0,0.5)",
            }} className="btn-hover">
              <MessageCircle size={12}/> 카카오
            </button>
          )}
          {isLoggedIn ? (
            <div style={{
              display:"flex", alignItems:"center", gap:7,
              background:"linear-gradient(135deg,#a855f7,#ec4899)",
              borderRadius:20, padding:"7px 14px",
              fontSize:12, fontWeight:800, color:"#fff",
              boxShadow:"0 4px 14px rgba(168,85,247,0.4)",
            }}>
              {userProfile?.imageUrl
                ? <img src={userProfile.imageUrl} alt="" style={{width:18,height:18,borderRadius:"50%"}}/>
                : <User size={14}/>}
              {userProfile?.nickname || "내정보"}
            </div>
          ) : (
            <button onClick={()=>setLoginModal(true)} style={{
              background:"linear-gradient(135deg,#a855f7,#ec4899)",
              color:"#fff", border:"none", borderRadius:20,
              padding:"7px 16px", fontWeight:800, fontSize:12, cursor:"pointer",
              boxShadow:"0 4px 14px rgba(168,85,247,0.4)",
              display:"flex", alignItems:"center", gap:4,
            }} className="btn-hover">
              {isPC ? <><BellRing size={13}/> 최저가 알림받기</> : <><Plus size={14}/> 등록</>}
            </button>
          )}
        </div>
      </header>

      {/* ════ 하단 네비 (모바일 전용) ════ */}
      {!isPC && (
        <nav style={{
          position:"fixed", bottom:0, left:0, right:0,
          background:"rgba(255,255,255,0.95)",
          backdropFilter:"blur(20px)",
          borderTop:"1px solid rgba(192,132,252,0.12)",
          display:"flex", zIndex:200,
          paddingBottom:"env(safe-area-inset-bottom)",
        }}>
          {[
            ["home",      <Home size={22} strokeWidth={tab==="home"?2.5:1.8}/>,      "홈"],
            ["search",    <Search size={22} strokeWidth={tab==="search"?2.5:1.8}/>,  "탐색"],
            ["watchlist", <Heart size={22} strokeWidth={tab==="watchlist"?2.5:1.8}/>, "관심"],
            ["alerts",    <Bell size={22} strokeWidth={tab==="alerts"?2.5:1.8}/>,    "알림"],
            ["mypage",    <User size={22} strokeWidth={tab==="mypage"?2.5:1.8}/>,    "MY"],
          ].map(([key,icon,label])=>(
            <button key={key} onClick={()=>setTab(key)} style={{
              flex:1, border:"none", background:"none", cursor:"pointer",
              display:"flex", flexDirection:"column", alignItems:"center", gap:3,
              padding:"10px 0 8px",
              color: tab===key ? "#a855f7" : "#cbd5e1",
              transition:"color 0.15s",
            }}>
              <span style={{filter:tab===key?"drop-shadow(0 0 6px rgba(168,85,247,0.5))":"none"}}>{icon}</span>
              <span style={{fontSize:9,fontWeight:tab===key?800:500}}>{label}</span>
              {tab===key && <div style={{width:18,height:2,borderRadius:2,background:"linear-gradient(90deg,#a855f7,#ec4899)",marginTop:1}}/>}
            </button>
          ))}
        </nav>
      )}

      {/* ════ 메인 콘텐츠 ════ */}
      <main style={{
        padding: isPC ? "28px 40px 48px" : "16px 16px 90px",
        maxWidth: isPC ? 1280 : 600,
        margin: "0 auto",
        opacity: mounted ? 1 : 0,
        transition: "opacity 0.3s",
      }}>

        {/* ══ 홈 ══ */}
        {tab==="home" && (
          <div style={{display: isPC ? "grid" : "block", gridTemplateColumns: isPC ? "1fr 340px" : undefined, gap: isPC ? 28 : 0}}>

            {/* ── 왼쪽/전체 컬럼 ── */}
            <div>
              {/* 모바일: 검색바 */}
              {!isPC && (
                <div style={{
                  background:"#fff", borderRadius:18, padding:"11px 16px",
                  display:"flex", alignItems:"center", gap:10, marginBottom:18,
                  boxShadow:"0 2px 16px rgba(168,85,247,0.07)",
                  border:"1.5px solid rgba(192,132,252,0.15)",
                }}>
                  <span style={{fontSize:16,color:"#c4b5fd"}}>🔍</span>
                  <input placeholder="상품명으로 검색하세요..."
                    value={searchText} onChange={e=>setSearchText(e.target.value)}
                    onFocus={()=>setTab("search")}
                    style={{border:"none",outline:"none",flex:1,fontSize:13,background:"transparent",color:"#18181b"}}/>
                  <span style={{fontSize:11,color:"#c4b5fd",fontWeight:600,background:"#f5f0ff",padding:"3px 8px",borderRadius:8}}>검색</span>
                </div>
              )}

              {/* 히어로 배너 */}
              <div style={{
                background:"linear-gradient(135deg,#7c3aed 0%,#a855f7 50%,#ec4899 100%)",
                borderRadius:24, padding: isPC ? "32px 32px" : "24px 22px",
                marginBottom:24, color:"#fff", position:"relative", overflow:"hidden",
                boxShadow:"0 8px 32px rgba(124,58,237,0.35)",
              }}>
                <div style={{position:"absolute",right:-30,top:-30,width:200,height:200,borderRadius:"50%",background:"rgba(255,255,255,0.06)"}}/>
                <div style={{position:"absolute",right:20,bottom:-20,width:120,height:120,borderRadius:"50%",background:"rgba(255,255,255,0.04)"}}/>
                <div style={{position:"relative",zIndex:1}}>
                  <div style={{
                    fontSize:9,fontWeight:700,letterSpacing:"2px",
                    background:"rgba(255,255,255,0.2)",borderRadius:20,padding:"4px 10px",
                    display:"inline-block",marginBottom:10,
                  }}>🧚 요정이 찾아낸 오늘의 특가</div>
                  <div style={{fontSize: isPC ? 26 : 21,fontWeight:900,lineHeight:1.3,marginBottom:6}}>
                    유니클로 히트텍<br/>
                    <span style={{color:"#fde68a"}}>-33%</span> 할인 중
                  </div>
                  <div style={{fontSize:12,opacity:0.85,marginBottom:16}}>19,900원 → 목표가 14,900원까지 ↓</div>
                  <div style={{display:"flex",gap:8}}>
                    <button style={{
                      background:"rgba(255,255,255,0.95)",color:"#7c3aed",
                      border:"none",borderRadius:14,padding:"9px 18px",
                      fontSize:12,fontWeight:800,cursor:"pointer",
                    }} className="btn-hover">지금 구매 →</button>
                    <button onClick={()=>{ if(!isLoggedIn){ setLoginTarget("target"); setLoginModal(true); } }}
                      style={{
                      background:"rgba(255,255,255,0.15)",color:"#fff",
                      border:"1.5px solid rgba(255,255,255,0.3)",borderRadius:14,
                      padding:"9px 18px",fontSize:12,fontWeight:700,cursor:"pointer",
                    }} className="btn-hover">알림 설정</button>
                  </div>
                </div>
              </div>

              {/* 통계 바 */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:isPC?14:10,marginBottom:isPC?24:20}}>
                {[
                  {label:"모니터링 상품",value:`${products.length}개`,   icon:<Package size={22} color="#a855f7"/>, color:"#a855f7"},
                  {label:"오늘 절약 유저",value:"2,847명",                icon:<Users size={22} color="#ec4899"/>,   color:"#ec4899"},
                  {label:"평균 절약금액",value:"23,400원",                icon:<TrendingDown size={22} color="#f59e0b"/>,color:"#f59e0b"},
                ].map((s,i)=>(
                  <div key={s.label} style={{
                    background:"#fff",borderRadius:18,padding: isPC ? "18px 14px" : "14px 12px",
                    border:"1.5px solid rgba(192,132,252,0.12)",
                    boxShadow:"0 2px 12px rgba(168,85,247,0.05)",textAlign:"center",
                    animation:`fadeUp 0.4s ease ${i*0.08}s forwards`,opacity:0,
                  }}>
                    <div style={{marginBottom:5}}>{s.icon}</div>
                    <div style={{fontWeight:900,fontSize:isPC?17:15,color:s.color}}>{s.value}</div>
                    <div style={{fontSize:9,color:"#94a3b8",marginTop:3,fontWeight:600}}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* 카테고리 */}
              <div style={{marginBottom:isPC?24:20}}>
                <div style={{fontWeight:800,fontSize:14,marginBottom:12}}>카테고리</div>
                <div style={isPC
                  ? {display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8}
                  : {display:"flex",gap:8,overflowX:"auto",paddingBottom:6,scrollbarWidth:"none"}
                }>
                  {CATEGORIES.map(c=>(
                    <button key={c.key}
                      onClick={()=>{setActiveCat(c.key); if(!isPC) setTab("search");}}
                      style={{
                        display:"flex",flexDirection:"column",alignItems:"center",gap:5,
                        background:"#fff",
                        border:`1.5px solid ${activeCat===c.key ? catColor(c.key) : "rgba(192,132,252,0.15)"}`,
                        borderRadius:16,padding: isPC ? "12px 8px" : "10px 12px",
                        cursor:"pointer",minWidth: isPC ? "auto" : 64,
                        transition:"all 0.15s",
                        boxShadow:activeCat===c.key ? `0 4px 14px ${catColor(c.key)}30` : "none",
                        flexShrink: isPC ? undefined : 0,
                      }}>
                      <span style={{fontSize:22}}>{c.icon}</span>
                      <span style={{fontSize:9,fontWeight:700,
                        color:activeCat===c.key ? catColor(c.key) : "#94a3b8",
                        whiteSpace:"nowrap",
                      }}>{c.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* PC: 카테고리 필터된 상품 그리드 */}
              {isPC && (
                <div style={{marginBottom:28}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                    <div style={{fontWeight:800,fontSize:15,display:"flex",alignItems:"center",gap:6}}>
                      <ShoppingCart size={16} color="#a855f7"/> 최저가 상품
                    </div>
                    <span style={{fontSize:11,color:"#94a3b8"}}>{filtered.length}개 상품</span>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
                    {filtered.slice(0,8).map((p,i)=>{
                      const color = catColor(p.category);
                      return (
                        <div key={p.id} onClick={()=>{setSelected(p);setTab("detail");}}
                          className="card-hover"
                          style={{...cardBase,animation:`fadeUp 0.4s ease ${i*0.05}s forwards`,opacity:0,display:"flex",flexDirection:"column"}}>
                          {/* 이미지 or 이모지 */}
                          <div style={{
                            width:"100%",height:80,borderRadius:12,background:`${color}10`,
                            display:"flex",alignItems:"center",justifyContent:"center",
                            marginBottom:10,overflow:"hidden",flexShrink:0,
                          }}>
                            {p.imageUrl
                              ? <img src={p.imageUrl} alt={p.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                              : <span style={{fontSize:32}}>{p.icon}</span>}
                          </div>
                          <div style={{fontSize:9,background:`${color}15`,color,borderRadius:6,padding:"2px 7px",fontWeight:700,display:"inline-block",marginBottom:6}}>{catLabel(p.category)}</div>
                          <div style={{fontWeight:700,fontSize:12,lineHeight:1.4,marginBottom:6,flex:1,
                            overflow:"hidden",textOverflow:"ellipsis",
                            display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",
                          }}>{p.name}</div>
                          {productsLoading && p.price === 0
                            ? <div style={{height:20,background:"#f5f0ff",borderRadius:6,marginBottom:8,animation:"fadeUp 1s infinite"}}/>
                            : <div style={{fontWeight:900,fontSize:16,color,marginBottom:4}}>
                                {p.price > 0 ? fmt(p.price) : '-'}
                              </div>
                          }
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                            {p.drop < 0
                              ? <span style={{fontSize:10,color:"#10b981",fontWeight:700}}>{p.drop}% ↓</span>
                              : <span/>
                            }
                            {p.history.length >= 2 && <MiniChart data={p.history} color={color}/>}
                          </div>
                          <button
                            onClick={e=>{e.stopPropagation();setSelected(p);setTab("detail");}}
                            style={{
                              display:"flex",alignItems:"center",justifyContent:"center",gap:4,
                              background:"linear-gradient(135deg,#a855f7,#ec4899)",
                              color:"#fff",borderRadius:10,padding:"7px 0",
                              fontSize:11,fontWeight:800,border:"none",cursor:"pointer",
                              boxShadow:"0 2px 8px rgba(168,85,247,0.25)",
                              transition:"all 0.15s",width:"100%",
                            }}
                            className="btn-hover">
                            <ShoppingCart size={11}/> 최저가 바로가기
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 가격 폭락 TOP */}
              <div style={{marginBottom:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <div style={{width:4,height:16,borderRadius:2,background:"linear-gradient(180deg,#a855f7,#ec4899)"}}/>
                    <span style={{fontWeight:800,fontSize:14,display:"flex",alignItems:"center",gap:5}}>
                      <TrendingDown size={15} color="#a855f7"/> 가격 폭락 TOP
                    </span>
                  </div>
                  <button onClick={()=>setTab("search")} style={{
                    fontSize:11,color:"#a855f7",background:"#f5f0ff",border:"none",
                    cursor:"pointer",fontWeight:700,borderRadius:10,padding:"4px 10px",
                  }}>전체보기 →</button>
                </div>
                <div style={{display:"grid",gridTemplateColumns: isPC ? "repeat(4,1fr)" : "1fr 1fr",gap:isPC?14:10}}>
                  {topDrops.map((p,i)=>{
                    const color = catColor(p.category);
                    const progress = p.high > p.low
                      ? Math.min(100,Math.round(((p.high-p.price)/(p.high-p.low))*100))
                      : 0;
                    return (
                      <div key={p.id} onClick={()=>{setSelected(p);setTab("detail");}}
                        className="card-hover"
                        style={{...cardBase,animation:`fadeUp 0.4s ease ${i*0.06}s forwards`,opacity:0,display:"flex",flexDirection:"column"}}>
                        {/* 이미지 */}
                        <div style={{width:"100%",height:60,borderRadius:10,overflow:"hidden",background:`${color}10`,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:8,flexShrink:0}}>
                          {p.imageUrl
                            ? <img src={p.imageUrl} alt={p.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                            : <span style={{fontSize:24}}>{p.icon}</span>}
                        </div>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                          <div style={{fontSize:9,background:`${color}15`,color,borderRadius:6,padding:"2px 6px",fontWeight:700}}>{catLabel(p.category)}</div>
                          <span style={{
                            fontSize:11,fontWeight:800,color:"#fff",
                            background:`linear-gradient(135deg,${color},${color}cc)`,
                            borderRadius:8,padding:"2px 7px",
                          }}>{p.drop}%</span>
                        </div>
                        <div style={{fontSize:11,fontWeight:700,lineHeight:1.4,marginBottom:6,color:"#374151",flex:1,
                          overflow:"hidden",textOverflow:"ellipsis",
                          display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",
                        }}>{p.name}</div>
                        <div style={{fontWeight:900,fontSize:15,color,marginBottom:6}}>{fmt(p.price)}</div>
                        <div style={{height:4,background:"#f1f0f5",borderRadius:10,overflow:"hidden",marginBottom:4}}>
                          <div style={{width:`${progress}%`,height:"100%",background:`linear-gradient(90deg,${color}80,${color})`,borderRadius:10}}/>
                        </div>
                        <div style={{fontSize:9,color:"#94a3b8",display:"flex",justifyContent:"space-between",marginBottom:8}}>
                          <span>👀{p.watching}</span><span>{p.store}</span>
                        </div>
                        <button
                          onClick={e=>{e.stopPropagation();setSelected(p);setTab("detail");}}
                          style={{
                            display:"flex",alignItems:"center",justifyContent:"center",gap:4,
                            background:"linear-gradient(135deg,#a855f7,#ec4899)",
                            color:"#fff",borderRadius:9,padding:"6px 0",
                            fontSize:10,fontWeight:800,border:"none",cursor:"pointer",
                            boxShadow:"0 2px 6px rgba(168,85,247,0.2)",width:"100%",
                          }}
                          className="btn-hover">
                          <ShoppingCart size={10}/> 최저가 바로가기
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 핫딜 */}
              <div style={{marginBottom:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <div style={{width:4,height:16,borderRadius:2,background:"linear-gradient(180deg,#f59e0b,#ef4444)"}}/>
                    <span style={{fontWeight:800,fontSize:14}}>🔥 오늘의 핫딜</span>
                  </div>
                  <span style={{fontSize:10,color:"#ef4444",fontWeight:700,background:"#fef2f2",padding:"3px 8px",borderRadius:8}}>역대 최저가</span>
                </div>
                <div style={{
                  display: isPC ? "grid" : "flex",
                  gridTemplateColumns: isPC ? "repeat(2,1fr)" : undefined,
                  flexDirection: isPC ? undefined : "column",
                  gap: isPC ? 12 : 8,
                }}>
                  {HOTDEALS.map((d,i)=>{
                    const color = catColor(d.category);
                    const sec = countdown[d.id] || 0;
                    const urgent = sec < 3600;
                    return (
                      <div key={d.id} className="card-hover" style={{
                        background:"#fff",borderRadius:18,padding:"14px 16px",
                        border:`1.5px solid ${urgent?"#fecaca":"rgba(192,132,252,0.1)"}`,
                        display:"flex",alignItems:"center",gap:12,cursor:"pointer",
                        boxShadow:urgent?"0 2px 16px rgba(239,68,68,0.1)":"0 1px 8px rgba(168,85,247,0.04)",
                        animation:`fadeUp 0.4s ease ${i*0.07}s forwards`,opacity:0,
                      }}>
                        <div style={{width:48,height:48,borderRadius:14,background:`${color}12`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,flexShrink:0}}>{d.icon}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontWeight:700,fontSize:12,marginBottom:4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.name}</div>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <span style={{fontSize:9,background:urgent?"#fef2f2":"#f5f0ff",color:urgent?"#ef4444":color,borderRadius:6,padding:"2px 7px",fontWeight:700}}>⏱ {fmtTime(sec)}</span>
                            <span style={{fontSize:9,color:"#94a3b8"}}>{d.store}</span>
                          </div>
                        </div>
                        <div style={{textAlign:"right",flexShrink:0}}>
                          <div style={{fontWeight:900,fontSize:15,color:"#ef4444"}}>{d.price.toLocaleString()}원</div>
                          <div style={{fontSize:10,color:"#94a3b8",textDecoration:"line-through"}}>{d.original.toLocaleString()}원</div>
                          <div style={{fontSize:10,fontWeight:800,color:"#fff",background:"linear-gradient(135deg,#ef4444,#f97316)",borderRadius:6,padding:"2px 6px",marginTop:2}}>-{d.discount}%</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 가격 동향 */}
              <div style={{marginBottom:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <div style={{width:4,height:16,borderRadius:2,background:"linear-gradient(180deg,#ef4444,#10b981)"}}/>
                    <span style={{fontWeight:800,fontSize:14,display:"flex",alignItems:"center",gap:5}}>
                      <BarChart2 size={15} color="#a855f7"/> 이번주 가격 동향
                    </span>
                  </div>
                  <div style={{display:"flex",gap:4}}>
                    {[["drop",<TrendingDown size={11}/>,"급락"],["surge",<TrendingUp size={11}/>,"급등"]].map(([k,icon,l])=>(
                      <button key={k} onClick={()=>setHotTab(k)} style={{
                        fontSize:10,fontWeight:700,border:"none",cursor:"pointer",borderRadius:10,padding:"4px 10px",
                        display:"flex",alignItems:"center",gap:3,
                        background:hotTab===k?(k==="drop"?"#dcfce7":"#fef2f2"):"#f8f7ff",
                        color:hotTab===k?(k==="drop"?"#10b981":"#ef4444"):"#94a3b8",
                      }}>{icon}{l}</button>
                    ))}
                  </div>
                </div>
                <div style={{
                  display: isPC ? "grid" : "flex",
                  gridTemplateColumns: isPC ? "repeat(2,1fr)" : undefined,
                  flexDirection: isPC ? undefined : "column",
                  gap: isPC ? 10 : 6,
                }}>
                  {(hotTab==="drop"?PRICE_DROP:PRICE_SURGE).map((p,i)=>{
                    const isDown = hotTab==="drop";
                    const pct = isDown ? p.drop : p.rise;
                    const color = isDown ? "#10b981" : "#ef4444";
                    const barW = Math.min(100, Math.abs(pct)*1.5);
                    return (
                      <div key={i} style={{
                        background:"#fff",borderRadius:16,padding:"12px 14px",
                        border:"1.5px solid rgba(192,132,252,0.08)",
                        animation:`fadeUp 0.3s ease ${i*0.06}s forwards`,opacity:0,
                      }}>
                        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                          <span style={{fontSize:22}}>{p.icon}</span>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontWeight:700,fontSize:11,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginBottom:2}}>{p.name}</div>
                            <div style={{display:"flex",alignItems:"center",gap:6}}>
                              <span style={{fontWeight:900,fontSize:13,color}}>{p.price.toLocaleString()}원</span>
                              <span style={{fontSize:9,color:"#94a3b8",textDecoration:"line-through"}}>{p.prev.toLocaleString()}원</span>
                            </div>
                          </div>
                          <div style={{fontSize:12,fontWeight:900,color:"#fff",background:`linear-gradient(135deg,${color},${color}cc)`,borderRadius:10,padding:"4px 10px",flexShrink:0}}>
                            {isDown?"":"+"}{pct}%
                          </div>
                        </div>
                        <div style={{height:4,background:"#f1f0f5",borderRadius:10,overflow:"hidden"}}>
                          <div style={{width:`${barW}%`,height:"100%",background:`linear-gradient(90deg,${color}60,${color})`,borderRadius:10}}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{marginTop:10,padding:"8px 12px",background:"#f8f7ff",borderRadius:10,fontSize:9,color:"#94a3b8",textAlign:"center",fontWeight:600}}>
                  절약요정 가격 데이터 기준 · 쿠팡/네이버 실시간 수집
                </div>
              </div>
            </div>

            {/* ── PC: 우측 사이드바 ── */}
            {isPC && (
              <div style={{display:"flex",flexDirection:"column",gap:16}}>

                {/* 카카오 채널 */}
                <div onClick={()=>setKakaoModal(true)} style={{
                  background:"#FEE500",borderRadius:18,padding:"16px 18px",
                  display:"flex",alignItems:"center",gap:12,cursor:"pointer",
                  boxShadow:"0 4px 16px rgba(254,229,0,0.4)",
                }} className="card-hover">
                  <div style={{width:42,height:42,borderRadius:12,background:"#FEE500",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <MessageCircle size={22} color="#3A1D1D"/>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:800,fontSize:13,color:"#3A1D1D"}}>카카오채널 구독하기</div>
                    <div style={{fontSize:11,color:"#5C3317",marginTop:2}}>가격 알림을 카톡으로 받아보세요</div>
                  </div>
                  <ChevronRight size={16} color="#3A1D1D"/>
                </div>

                {/* 최근 알림 */}
                <div style={{background:"#fff",borderRadius:18,padding:18,border:"1.5px solid rgba(192,132,252,0.12)"}}>
                  <div style={{fontWeight:800,fontSize:14,marginBottom:14,display:"flex",alignItems:"center",gap:6}}>
                    <BellRing size={15} color="#a855f7"/> 최근 알림
                  </div>
                  {ALERTS.slice(0,3).map((a,i)=>{
                    const color = catColor(a.category);
                    return (
                      <div key={a.id} style={{
                        display:"flex",alignItems:"flex-start",gap:10,
                        paddingBottom:12,marginBottom:12,
                        borderBottom:i<2?"1px solid rgba(192,132,252,0.1)":"none",
                      }}>
                        <div style={{width:32,height:32,borderRadius:10,background:`${color}15`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                          <Bell size={14} color={color}/>
                        </div>
                        <div style={{flex:1}}>
                          <div style={{fontWeight:700,fontSize:12}}>{a.product}</div>
                          <div style={{fontSize:11,color:"#64748b",marginTop:2}}>{a.msg}</div>
                          <div style={{fontSize:10,color:"#c4b5fd",marginTop:3}}>{a.time}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* 관심 상품 */}
                <div style={{background:"#fff",borderRadius:18,padding:18,border:"1.5px solid rgba(192,132,252,0.12)"}}>
                  <div style={{fontWeight:800,fontSize:14,marginBottom:14,display:"flex",alignItems:"center",gap:6}}>
                    <Heart size={15} color="#a855f7"/> 관심 상품
                  </div>
                  {products.slice(0,4).map((p,i)=>{
                    const color = catColor(p.category);
                    return (
                      <div key={p.id} onClick={()=>{setSelected(p);setTab("detail");}}
                        style={{
                          display:"flex",alignItems:"center",gap:10,cursor:"pointer",
                          paddingBottom:10,marginBottom:10,
                          borderBottom:i<3?"1px solid rgba(192,132,252,0.08)":"none",
                        }} className="btn-hover">
                        <div style={{width:36,height:36,borderRadius:10,background:`${color}12`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>{p.icon}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontWeight:600,fontSize:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</div>
                          <div style={{fontSize:10,color:"#94a3b8"}}>{p.store}</div>
                        </div>
                        <div style={{fontWeight:900,fontSize:13,color,flexShrink:0}}>{fmt(p.price)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ 탐색 ══ */}
        {tab==="search" && (
          <div>
            <div style={{fontWeight:800,fontSize:16,marginBottom:14}}>🔍 상품 탐색</div>
            <div style={{
              background:"#fff",borderRadius:18,padding:"11px 16px",
              display:"flex",alignItems:"center",gap:10,marginBottom:14,
              boxShadow:"0 2px 12px rgba(168,85,247,0.07)",
              border:"1.5px solid rgba(192,132,252,0.15)",
            }}>
              <Search size={15} color="#c4b5fd"/>
              <input placeholder="상품명 검색... (Enter)" value={searchText}
                onChange={e=>setSearchText(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                style={{border:"none",outline:"none",flex:1,fontSize:13,background:"transparent"}}/>
              {searchText && <button onClick={()=>setSearchText("")} style={{background:"none",border:"none",cursor:"pointer",color:"#c4b5fd",fontSize:16}}>✕</button>}
              <button onClick={()=>handleSearch()} disabled={isSearching || !searchText} style={{
                background:"linear-gradient(135deg,#a855f7,#ec4899)",color:"#fff",
                border:"none",borderRadius:20,padding:"5px 12px",fontSize:11,fontWeight:700,
                cursor:searchText?"pointer":"default",opacity:searchText?1:0.4,
              }}>
                {isSearching ? "검색중..." : "검색"}
              </button>
            </div>
            <div style={{display:"grid",gridTemplateColumns: isPC ? "repeat(10,1fr)" : "repeat(5,1fr)",gap:6,marginBottom:14}}>
              {CATEGORIES.map(c=>(
                <button key={c.key} onClick={()=>setActiveCat(c.key)} style={{
                  display:"flex",flexDirection:"column",alignItems:"center",gap:3,
                  background:activeCat===c.key?catColor(c.key):"#fff",
                  border:`1.5px solid ${activeCat===c.key?catColor(c.key):"rgba(192,132,252,0.15)"}`,
                  borderRadius:14,padding:"8px 4px",cursor:"pointer",transition:"all 0.15s",
                  boxShadow:activeCat===c.key?`0 4px 12px ${catColor(c.key)}40`:"none",
                }}>
                  <span style={{fontSize:18}}>{c.icon}</span>
                  <span style={{fontSize:8,fontWeight:700,whiteSpace:"nowrap",color:activeCat===c.key?"#fff":"#94a3b8"}}>{c.label}</span>
                </button>
              ))}
            </div>
            {/* 네이버 실검색 결과 */}
            {isSearching && (
              <div style={{textAlign:"center",padding:"60px 0",color:"#c4b5fd"}}>
                <div style={{fontSize:32,marginBottom:12,animation:"float 1s ease-in-out infinite"}}>🔍</div>
                <div style={{fontSize:14,fontWeight:600}}>네이버 쇼핑 검색 중...</div>
              </div>
            )}

            {searchError && !isSearching && (
              <div style={{textAlign:"center",padding:"40px 0",color:"#f43f5e",background:"#fff5f5",borderRadius:16,margin:"8px 0"}}>
                <div style={{fontSize:24,marginBottom:8}}>⚠️</div>
                <div style={{fontSize:13,fontWeight:600}}>{searchError}</div>
              </div>
            )}

            {!isSearching && !searchError && naverResults.length > 0 && (
              <>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <span style={{fontSize:11,color:"#94a3b8",fontWeight:600}}>네이버 쇼핑 {naverResults.length}개 결과</span>
                  <span style={{fontSize:10,color:"#c4b5fd",fontWeight:600}}>powered by NAVER</span>
                </div>
                <div style={{
                  display: isPC ? "grid" : "flex",
                  gridTemplateColumns: isPC ? "repeat(4,1fr)" : undefined,
                  flexDirection: isPC ? undefined : "column",
                  gap: isPC ? 14 : 8,
                }}>
                  {naverResults.map(p => {
                    const discountColor = "#10b981";
                    return (
                      <a key={p.id} href={p.link} target="_blank" rel="noopener noreferrer"
                        className="card-hover"
                        style={{
                          textDecoration:"none",color:"inherit",
                          ...(isPC ? {...cardBase,animation:`fadeUp 0.3s ease forwards`,opacity:0,display:"block"} : {
                            background:"#fff",borderRadius:18,padding:"14px 16px",
                            border:"1.5px solid rgba(192,132,252,0.1)",
                            display:"flex",alignItems:"center",gap:12,
                            boxShadow:"0 1px 8px rgba(168,85,247,0.04)",
                          })
                        }}>
                        {isPC ? (
                          <>
                            <div style={{width:52,height:52,borderRadius:14,overflow:"hidden",marginBottom:10,background:"#f5f0ff",display:"flex",alignItems:"center",justifyContent:"center"}}>
                              {p.imageUrl
                                ? <img src={p.imageUrl} alt={p.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                                : <span style={{fontSize:28}}>{p.icon}</span>}
                            </div>
                            <div style={{fontSize:9,background:"#f5f0ff",color:"#a855f7",borderRadius:6,padding:"2px 7px",fontWeight:700,display:"inline-block",marginBottom:6}}>{p.category}</div>
                            <div style={{fontWeight:700,fontSize:12,lineHeight:1.4,marginBottom:8,overflow:"hidden",textOverflow:"ellipsis",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{p.name}</div>
                            <div style={{fontWeight:900,fontSize:16,color:"#a855f7"}}>{fmt(p.price)}</div>
                            {p.discountRate > 0 && (
                              <div style={{fontSize:10,color:discountColor,fontWeight:700,marginTop:4}}>{p.discountRate}% ↓ <span style={{color:"#94a3b8",textDecoration:"line-through"}}>{fmt(p.originalPrice)}</span></div>
                            )}
                            <div style={{fontSize:10,color:"#94a3b8",marginTop:4}}>{p.store}{p.brand ? ` · ${p.brand}` : ""}</div>
                          </>
                        ) : (
                          <>
                            <div style={{width:52,height:52,borderRadius:14,overflow:"hidden",flexShrink:0,background:"#f5f0ff",display:"flex",alignItems:"center",justifyContent:"center"}}>
                              {p.imageUrl
                                ? <img src={p.imageUrl} alt={p.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                                : <span style={{fontSize:26}}>{p.icon}</span>}
                            </div>
                            <div style={{flex:1,minWidth:0}}>
                              <span style={{fontSize:9,background:"#f5f0ff",color:"#a855f7",borderRadius:6,padding:"2px 7px",fontWeight:700}}>{p.category}</span>
                              <div style={{fontWeight:700,fontSize:12,lineHeight:1.4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginTop:3}}>{p.name}</div>
                              <div style={{fontSize:10,color:"#94a3b8",marginTop:2}}>{p.store}{p.brand ? ` · ${p.brand}` : ""}</div>
                            </div>
                            <div style={{textAlign:"right",flexShrink:0}}>
                              <div style={{fontWeight:900,fontSize:15,color:"#a855f7"}}>{fmt(p.price)}</div>
                              {p.discountRate > 0 && (
                                <div style={{fontSize:10,color:discountColor,fontWeight:700,marginTop:2}}>{p.discountRate}% ↓</div>
                              )}
                            </div>
                          </>
                        )}
                      </a>
                    );
                  })}
                </div>
              </>
            )}

            {/* 네이버 검색 전: 로컬 상품 목록 */}
            {!isSearching && !searchError && naverResults.length === 0 && (
              <>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <span style={{fontSize:11,color:"#94a3b8",fontWeight:600}}>{filtered.length}개 상품</span>
                </div>
                <div style={{
                  display: isPC ? "grid" : "flex",
                  gridTemplateColumns: isPC ? "repeat(4,1fr)" : undefined,
                  flexDirection: isPC ? undefined : "column",
                  gap: isPC ? 14 : 8,
                }}>
                  {filtered.map(p=>{
                    const color = catColor(p.category);
                    return (
                      <div key={p.id} onClick={()=>{setSelected(p);setTab("detail");}}
                        className="card-hover"
                        style={isPC ? {...cardBase,animation:`fadeUp 0.3s ease forwards`,opacity:0} : {
                          background:"#fff",borderRadius:18,padding:"14px 16px",
                          border:"1.5px solid rgba(192,132,252,0.1)",
                          display:"flex",alignItems:"center",gap:12,cursor:"pointer",
                          boxShadow:"0 1px 8px rgba(168,85,247,0.04)",
                        }}>
                        {isPC ? (
                          <>
                            <div style={{width:52,height:52,borderRadius:14,background:`${color}12`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:30,marginBottom:10}}>{p.icon}</div>
                            <div style={{fontSize:9,background:`${color}15`,color,borderRadius:6,padding:"2px 7px",fontWeight:700,display:"inline-block",marginBottom:6}}>{catLabel(p.category)}</div>
                            <div style={{fontWeight:700,fontSize:12,lineHeight:1.4,marginBottom:8,overflow:"hidden",textOverflow:"ellipsis",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{p.name}</div>
                            <div style={{fontWeight:900,fontSize:16,color}}>{fmt(p.price)}</div>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:4}}>
                              <span style={{fontSize:10,color:"#10b981",fontWeight:700}}>{p.drop}% ↓</span>
                              <MiniChart data={p.history} color={color}/>
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{width:48,height:48,borderRadius:14,background:`${color}12`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,flexShrink:0}}>{p.icon}</div>
                            <div style={{flex:1,minWidth:0}}>
                              <span style={{fontSize:9,background:`${color}15`,color,borderRadius:6,padding:"2px 7px",fontWeight:700}}>{catIcon(p.category)} {catLabel(p.category)}</span>
                              <div style={{fontWeight:700,fontSize:12,lineHeight:1.4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginTop:3}}>{p.name}</div>
                              <div style={{fontSize:10,color:"#94a3b8",marginTop:2}}>{p.store} · 👀{p.watching}명</div>
                            </div>
                            <div style={{textAlign:"right",flexShrink:0}}>
                              <div style={{fontWeight:900,fontSize:15,color}}>{fmt(p.price)}</div>
                              <div style={{fontSize:10,color:"#10b981",fontWeight:700,marginTop:2}}>{p.drop}% ↓</div>
                              <MiniChart data={p.history} color={color}/>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                  {filtered.length===0 && (
                    <div style={{textAlign:"center",padding:"60px 0",color:"#c4b5fd",gridColumn:"1/-1"}}>
                      <div style={{fontSize:48,marginBottom:12}}>🔍</div>
                      <div style={{fontSize:14,fontWeight:600}}>검색 결과가 없어요</div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ══ 관심 상품 ══ */}
        {tab==="watchlist" && (
          <div>
            <div style={{fontWeight:800,fontSize:16,marginBottom:16,display:"flex",alignItems:"center",gap:8}}>
              <Heart size={18} color="#a855f7"/> 관심 상품
            </div>
            <div style={{
              display: isPC ? "grid" : "flex",
              gridTemplateColumns: isPC ? "repeat(2,1fr)" : undefined,
              flexDirection: isPC ? undefined : "column",
              gap: isPC ? 14 : 10,
            }}>
              {products.slice(0,6).map(p=>{
                const color = catColor(p.category);
                const pct = Math.round(((p.price-p.target)/p.price)*100);
                return (
                  <div key={p.id} onClick={()=>{setSelected(p);setTab("detail");}}
                    className="card-hover"
                    style={{...cardBase,padding:18}}>
                    <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
                      <div style={{width:44,height:44,borderRadius:14,background:`${color}12`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0}}>{p.icon}</div>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:700,fontSize:13}}>{p.name}</div>
                        <div style={{fontSize:10,color:"#94a3b8",marginTop:2}}>{p.store}</div>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontWeight:900,fontSize:17,color}}>{fmt(p.price)}</div>
                        <div style={{fontSize:10,color:"#10b981",fontWeight:700}}>{p.drop}% ↓</div>
                      </div>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:"#94a3b8",marginBottom:5}}>
                      <span>목표가 {fmt(p.target)}</span>
                      <span style={{color,fontWeight:700}}>목표까지 {pct}% 남음</span>
                    </div>
                    <div style={{height:5,background:"#f1f0f5",borderRadius:10,overflow:"hidden"}}>
                      <div style={{width:`${100-pct}%`,height:"100%",background:`linear-gradient(90deg,${color}60,${color})`,borderRadius:10}}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ══ 알림 내역 ══ */}
        {tab==="alerts" && (
          <div style={{display: isPC ? "grid" : "block", gridTemplateColumns: isPC ? "1fr 360px" : undefined, gap: isPC ? 24 : 0}}>
            <div>
              <div style={{fontWeight:800,fontSize:16,marginBottom:16,display:"flex",alignItems:"center",gap:8}}>
                <BellRing size={18} color="#a855f7"/> 요정 알림함
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {ALERTS.map((a,i)=>{
                  const color = catColor(a.category);
                  return (
                    <div key={a.id} style={{
                      background:"#fff",borderRadius:18,padding:"14px 16px",
                      border:`1.5px solid ${color}20`,display:"flex",alignItems:"center",gap:12,
                      boxShadow:"0 2px 10px rgba(168,85,247,0.04)",
                      animation:`fadeUp 0.35s ease ${i*0.07}s forwards`,opacity:0,
                    }}>
                      <div style={{width:42,height:42,borderRadius:14,background:`${color}15`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <Bell size={20} color={color}/>
                      </div>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:700,fontSize:12}}>{a.product}</div>
                        <div style={{fontSize:11,color:"#64748b",marginTop:2}}>{a.msg}</div>
                      </div>
                      <div style={{fontSize:10,color:"#c4b5fd",flexShrink:0}}>{a.time}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{marginTop: isPC ? 0 : 24}}>
              <div style={{background:"#fff",borderRadius:20,padding:20,border:"1.5px solid rgba(192,132,252,0.12)"}}>
                <div style={{fontWeight:800,fontSize:14,marginBottom:14,display:"flex",alignItems:"center",gap:6}}>
                  <Settings size={15} color="#a855f7"/> 알림 수신 방법
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:8,marginBottom:14}}>
                  {[["kakao",<MessageCircle size={14}/>,"카카오톡"],["sms",<User size={14}/>,"문자"],["email",<Search size={14}/>,"이메일"],["push",<Bell size={14}/>,"앱푸시"]].map(([key,icon,label])=>(
                    <button key={key} onClick={()=>setNotifyMethod(key)} style={{
                      padding:"11px",borderRadius:14,cursor:"pointer",fontSize:12,fontWeight:700,
                      border:notifyMethod===key?"2px solid #a855f7":"1.5px solid rgba(192,132,252,0.2)",
                      background:notifyMethod===key?"#f5f0ff":"#fff",color:notifyMethod===key?"#a855f7":"#94a3b8",
                      transition:"all 0.15s",display:"flex",alignItems:"center",justifyContent:"center",gap:6,
                    }}>{icon}{label}</button>
                  ))}
                </div>
                <button style={{
                  width:"100%",padding:"14px",background:"linear-gradient(135deg,#a855f7,#ec4899)",
                  color:"#fff",border:"none",borderRadius:14,fontWeight:800,fontSize:14,cursor:"pointer",
                  boxShadow:"0 4px 16px rgba(168,85,247,0.35)",
                }} className="btn-hover">저장하기</button>
              </div>
            </div>
          </div>
        )}

        {/* ══ MY 페이지 ══ */}
        {tab==="mypage" && (
          <div style={{maxWidth: isPC ? 480 : "100%"}}>
            <div style={{
              background:"linear-gradient(135deg,#7c3aed,#a855f7,#ec4899)",
              borderRadius:24,padding:"28px 22px",marginBottom:16,color:"#fff",
              textAlign:"center",boxShadow:"0 8px 32px rgba(124,58,237,0.35)",
              position:"relative",overflow:"hidden",
            }}>
              <div style={{position:"absolute",right:-20,top:-20,width:120,height:120,borderRadius:"50%",background:"rgba(255,255,255,0.06)"}}/>
              {isLoggedIn && userProfile?.imageUrl
                ? <img src={userProfile.imageUrl} alt="" style={{width:64,height:64,borderRadius:"50%",border:"3px solid rgba(255,255,255,0.3)",marginBottom:8}}/>
                : <div style={{fontSize:52,marginBottom:8,animation:"float 3s ease-in-out infinite"}}>🧚</div>
              }
              <div style={{fontWeight:900,fontSize:18,marginBottom:3}}>
                {isLoggedIn ? `${userProfile?.nickname || "절약요정"}님` : "절약요정 회원님"}
              </div>
              <div style={{fontSize:11,opacity:0.75,marginBottom:18}}>가입일: 2025.01.15</div>
              <div style={{display:"flex",gap:10,justifyContent:"center"}}>
                {[["모니터링",`${products.length}개`],["이달절약","23,400원"],["알림수신","47회"]].map(([l,v])=>(
                  <div key={l} style={{background:"rgba(255,255,255,0.15)",borderRadius:14,padding:"10px 14px",backdropFilter:"blur(10px)"}}>
                    <div style={{fontSize:14,fontWeight:900}}>{v}</div>
                    <div style={{fontSize:9,opacity:0.8,marginTop:2}}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
            <div onClick={()=>setKakaoModal(true)} style={{
              background:"#FEE500",borderRadius:18,padding:"14px 18px",
              display:"flex",alignItems:"center",gap:12,marginBottom:12,cursor:"pointer",
              boxShadow:"0 4px 16px rgba(254,229,0,0.4)",
            }} className="card-hover">
              <MessageCircle size={22} color="#3A1D1D"/>
              <div style={{flex:1}}>
                <div style={{fontWeight:800,fontSize:13,color:"#3A1D1D"}}>카카오채널 구독하기</div>
                <div style={{fontSize:11,color:"#5C3317",marginTop:2}}>가격 알림을 카톡으로 받아보세요!</div>
              </div>
              <span style={{color:"#3A1D1D",fontSize:18}}>→</span>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {[
                [<BarChart2 size={18} color="#a855f7"/>,"절약 리포트","이번 달 내역 보기"],
                [<Bell size={18} color="#a855f7"/>,"알림 설정","수신 방법 변경"],
                [<Heart size={18} color="#a855f7"/>,"관심 상품 관리","등록/삭제"],
                [<Package size={18} color="#a855f7"/>,"구매 히스토리","지난 구매 내역"],
                [<Gift size={18} color="#a855f7"/>,"친구 초대","초대하면 알림 슬롯 +5개"],
              ].map(([icon,title,sub])=>(
                <div key={title} style={{
                  background:"#fff",borderRadius:16,padding:"14px 18px",
                  border:"1.5px solid rgba(192,132,252,0.1)",
                  display:"flex",alignItems:"center",gap:12,cursor:"pointer",
                  boxShadow:"0 1px 6px rgba(168,85,247,0.04)",
                }} className="card-hover">
                  <div style={{width:36,height:36,borderRadius:10,background:"#f5f0ff",display:"flex",alignItems:"center",justifyContent:"center"}}>{icon}</div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:13}}>{title}</div>
                    <div style={{fontSize:11,color:"#94a3b8",marginTop:1}}>{sub}</div>
                  </div>
                  <ChevronRight size={16} color="#c4b5fd"/>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══ 상품 상세 ══ */}
        {tab==="detail" && selected && (() => {
          const color = catColor(selected.category);
          const chartData = getChartData(selected);
          const chartPrices = chartData.flatMap(d => [d.naverPrice, d.coupangPrice]).filter(Boolean);
          const yMin = chartPrices.length ? Math.min(...chartPrices) * 0.97 : 0;
          const yMax = chartPrices.length ? Math.max(...chartPrices) * 1.02 : 100;

          // 역대 최저가 날짜 (DB 데이터 있으면 실제, 없으면 null)
          const allDb = dbHistory[selected.id] || [];
          const allTimeLow = allDb.reduce((min, d) => {
            const p = d.naver_price;
            return (p > 0 && p < min.price) ? { price: p, date: d.recorded_date } : min;
          }, { price: selected.low || Infinity, date: null });
          return (
            <div>
              <button onClick={()=>setTab("home")} style={{
                background:"#fff",border:"1.5px solid rgba(192,132,252,0.2)",
                borderRadius:12,padding:"8px 16px",fontSize:12,color:"#a855f7",
                cursor:"pointer",marginBottom:16,fontWeight:700,
                boxShadow:"0 1px 6px rgba(168,85,247,0.08)",
              }}>← 뒤로</button>

              <div style={{
                display: isPC ? "grid" : "block",
                gridTemplateColumns: isPC ? "1fr 320px" : undefined,
                gap: isPC ? 24 : 0,
                alignItems: "start",
              }}>
                {/* 왼쪽: 상품 정보 + 차트 */}
                <div>
                  <div style={{
                    background:"#fff",borderRadius:24,padding:22,
                    border:"1.5px solid rgba(192,132,252,0.1)",
                    boxShadow:"0 4px 24px rgba(168,85,247,0.08)",
                    marginBottom:16,
                  }}>
                    {/* 상품 헤더 */}
                    <div style={{display:"flex",alignItems:"flex-start",gap:14,marginBottom:20}}>
                      <div style={{width:72,height:72,borderRadius:20,background:`${color}15`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:40,flexShrink:0,overflow:"hidden"}}>
                        {selected.imageUrl
                          ? <img src={selected.imageUrl} alt={selected.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                          : selected.icon}
                      </div>
                      <div style={{flex:1}}>
                        <span style={{fontSize:9,background:`${color}15`,color,borderRadius:6,padding:"3px 9px",fontWeight:700,display:"inline-block",marginBottom:6}}>
                          {catIcon(selected.category)} {catLabel(selected.category)}
                        </span>
                        <div style={{fontWeight:800,fontSize:16,lineHeight:1.4}}>{selected.name}</div>
                        <div style={{fontSize:10,color:"#94a3b8",marginTop:4}}>{selected.store} · 👀{selected.watching}명 보는 중</div>
                      </div>
                    </div>

                    {/* 가격 카드 3개 */}
                    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:20}}>
                      {[
                        ["현재가",  fmt(selected.price),  color,     null],
                        ["목표가",  fmt(selected.target), "#a855f7", null],
                        ["역대최저",fmt(allTimeLow.price !== Infinity ? allTimeLow.price : selected.low), "#10b981",
                          allTimeLow.date ? allTimeLow.date.slice(5).replace('-','/') : null],
                      ].map(([l,v,c,sub])=>(
                        <div key={l} style={{background:"#faf8ff",borderRadius:14,padding:"12px 8px",textAlign:"center",border:`1.5px solid ${c}15`}}>
                          <div style={{fontSize:9,color:"#94a3b8",marginBottom:4,fontWeight:600}}>{l}</div>
                          <div style={{fontWeight:900,fontSize:isPC?14:13,color:c}}>{v}</div>
                          {sub && <div style={{fontSize:8,color:c,marginTop:2,opacity:0.75}}>{sub}</div>}
                        </div>
                      ))}
                    </div>


                    {/* ── recharts 가격 그래프 ── */}
                    <div style={{marginBottom:8}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                        <div style={{fontWeight:700,fontSize:13,display:"flex",alignItems:"center",gap:6}}>
                          <span style={{width:4,height:14,borderRadius:2,display:"inline-block",background:`linear-gradient(180deg,${color},#ec4899)`}}/>
                          가격 히스토리
                        </div>
                        <div style={{display:"flex",gap:4}}>
                          {PERIODS.map(p=>(
                            <button key={p.key} onClick={()=>setChartPeriod(p.key)} style={{
                              padding:"4px 10px",borderRadius:8,fontSize:11,fontWeight:700,border:"none",cursor:"pointer",
                              background:chartPeriod===p.key?color:"#f5f0ff",
                              color:chartPeriod===p.key?"#fff":"#a855f7",
                              transition:"all 0.15s",
                            }}>{p.label}</button>
                          ))}
                        </div>
                      </div>

                      <div style={{background:"#faf8ff",borderRadius:16,padding:"12px 4px 4px",border:"1.5px solid rgba(192,132,252,0.08)"}}>
                        <ResponsiveContainer width="100%" height={180}>
                          <AreaChart data={chartData} margin={{top:8,right:8,left:-10,bottom:0}}>
                            <defs>
                              <linearGradient id={`gradNaver${selected.id}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#03c75a" stopOpacity={0.15}/>
                                <stop offset="95%" stopColor="#03c75a" stopOpacity={0}/>
                              </linearGradient>
                              <linearGradient id={`gradCoupang${selected.id}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#e8003d" stopOpacity={0.10}/>
                                <stop offset="95%" stopColor="#e8003d" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0e8ff" vertical={false}/>
                            <XAxis
                              dataKey="name"
                              tick={{fontSize:10,fill:"#94a3b8"}}
                              axisLine={false} tickLine={false}
                            />
                            <YAxis
                              domain={[yMin, yMax]}
                              tick={{fontSize:10,fill:"#94a3b8"}}
                              axisLine={false} tickLine={false}
                              tickFormatter={v=>`${(v/1000).toFixed(0)}k`}
                              width={36}
                            />
                            <Tooltip content={<ChartTooltip/>}/>
                            <ReferenceLine
                              y={selected.target} stroke="#a855f7"
                              strokeDasharray="5 4" strokeWidth={1.5}
                              label={{value:"목표가",position:"right",fontSize:9,fill:"#a855f7"}}
                            />
                            <ReferenceLine
                              y={selected.low} stroke="#10b981"
                              strokeDasharray="5 4" strokeWidth={1.5}
                              label={{value:"역대최저",position:"right",fontSize:9,fill:"#10b981"}}
                            />
                            <Area
                              type="monotone" dataKey="coupangPrice" name="쿠팡"
                              stroke="#e8003d" strokeWidth={2}
                              fill={`url(#gradCoupang${selected.id})`}
                              dot={{r:2.5,fill:"#e8003d",strokeWidth:1.5,stroke:"#fff"}}
                              activeDot={{r:4,stroke:"#e8003d",strokeWidth:2,fill:"#fff"}}
                            />
                            <Area
                              type="monotone" dataKey="naverPrice" name="네이버"
                              stroke="#03c75a" strokeWidth={2}
                              fill={`url(#gradNaver${selected.id})`}
                              dot={{r:2.5,fill:"#03c75a",strokeWidth:1.5,stroke:"#fff"}}
                              activeDot={{r:4,stroke:"#03c75a",strokeWidth:2,fill:"#fff"}}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                        <div style={{display:"flex",gap:14,justifyContent:"center",paddingBottom:8,fontSize:9,color:"#94a3b8"}}>
                          <span style={{display:"flex",alignItems:"center",gap:4}}>
                            <span style={{width:20,height:2,background:"#e8003d",display:"inline-block",borderRadius:2}}/>쿠팡
                          </span>
                          <span style={{display:"flex",alignItems:"center",gap:4}}>
                            <span style={{width:20,height:2,background:"#03c75a",display:"inline-block",borderRadius:2}}/>네이버
                          </span>
                          <span style={{display:"flex",alignItems:"center",gap:4}}>
                            <span style={{width:16,height:1.5,display:"inline-block",borderTop:"1.5px dashed #a855f7"}}/>목표가
                          </span>
                          <span style={{display:"flex",alignItems:"center",gap:4}}>
                            <span style={{width:16,height:1.5,display:"inline-block",borderTop:"1.5px dashed #10b981"}}/>역대최저
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 오른쪽 / 하단: 구매 + 알림 (v2 단일 카드) */}
                <div style={{marginTop: isPC ? 0 : 0}}>
                  <div style={{
                    background:"#fff",borderRadius:24,padding:22,
                    border:"1.5px solid rgba(192,132,252,0.1)",
                    boxShadow:"0 4px 24px rgba(168,85,247,0.08)",
                  }}>
                    {/* 구매하기 */}
                    <div style={{fontWeight:800,fontSize:13,marginBottom:12,display:"flex",alignItems:"center",gap:6}}>
                      <ShoppingCart size={14} color={color}/> 최저가로 구매하기
                    </div>

                    {selected.coupangUrl && (
                      <div style={{marginBottom:10}}>
                        <a href={selected.coupangUrl} target="_blank" rel="noopener noreferrer"
                          style={{
                            display:"flex",alignItems:"center",justifyContent:"space-between",
                            padding:"13px 16px",borderRadius:14,textDecoration:"none",
                            background:"linear-gradient(135deg,#e8003d,#ff4d6d)",
                            boxShadow:"0 3px 10px rgba(232,0,61,0.25)",
                          }} className="btn-hover">
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <ShoppingCart size={14} color="#fff"/>
                            <div>
                              <div style={{color:"#fff",fontWeight:800,fontSize:13}}>쿠팡 바로가기</div>
                              <div style={{color:"rgba(255,255,255,0.75)",fontSize:10}}>로켓배송 · 파트너스</div>
                            </div>
                          </div>
                          <div style={{textAlign:"right"}}>
                            <div style={{color:"#fff",fontWeight:900,fontSize:14}}>
                              {selected.price > 0 ? fmt(selected.price) : "확인하기"}
                            </div>
                            <div style={{color:"rgba(255,255,255,0.75)",fontSize:10}}>바로가기 →</div>
                          </div>
                        </a>
                        <p style={{margin:"4px 2px 0",fontSize:9,color:"#e8003d",lineHeight:1.6,opacity:0.75}}>
                          이 사이트는 쿠팡 파트너스 활동의 일환으로, 이에 따른 일정액의 수수료를 제공받습니다.
                        </p>
                      </div>
                    )}

                    {selected.naverUrl && (
                      <div style={{marginBottom:16}}>
                        <a href={selected.naverUrl} target="_blank" rel="noopener noreferrer"
                          style={{
                            display:"flex",alignItems:"center",justifyContent:"space-between",
                            padding:"13px 16px",borderRadius:14,textDecoration:"none",
                            background:"linear-gradient(135deg,#03c75a,#00b84c)",
                            boxShadow:"0 3px 10px rgba(3,199,90,0.25)",
                          }} className="btn-hover">
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <ShoppingCart size={14} color="#fff"/>
                            <div>
                              <div style={{color:"#fff",fontWeight:800,fontSize:13}}>네이버 바로가기</div>
                              <div style={{color:"rgba(255,255,255,0.75)",fontSize:10}}>최저가 · 가격비교</div>
                            </div>
                          </div>
                          <div style={{textAlign:"right"}}>
                            <div style={{color:"#fff",fontWeight:900,fontSize:14}}>
                              {selected.price > 0 ? fmt(selected.price) : "클릭 후 확인"}
                            </div>
                            <div style={{color:"rgba(255,255,255,0.75)",fontSize:10}}>→</div>
                          </div>
                        </a>
                        <p style={{margin:"4px 2px 0",fontSize:9,color:"#03c75a",lineHeight:1.6,opacity:0.75}}>
                          이 사이트는 네이버 쇼핑커넥트 활동의 일환으로, 판매 발생 시 수수료를 제공받습니다.
                        </p>
                      </div>
                    )}

                    {/* 구분선 */}
                    <div style={{height:1,background:"rgba(192,132,252,0.12)",marginBottom:16}}/>

                    {/* 목표가 설정 */}
                    <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>🎯 목표가 설정</div>
                    <div style={{display:"flex",gap:8,marginBottom:12}}>
                      <input
                        value={targetInput || selected.target}
                        onChange={e=>setTargetInput(e.target.value)}
                        style={{
                          flex:1,padding:"12px 14px",borderRadius:12,
                          border:"1.5px solid rgba(192,132,252,0.25)",
                          fontSize:15,fontWeight:700,color:"#a855f7",outline:"none",
                          background:"#faf8ff",
                        }}
                        placeholder="목표가 입력"
                      />
                      <button onClick={()=>{
                        if(!isLoggedIn){ setLoginTarget("target"); setLoginModal(true); return; }
                      }} style={{
                        padding:"12px 18px",background:`linear-gradient(135deg,${color},#a855f7)`,
                        color:"#fff",border:"none",borderRadius:12,fontWeight:700,fontSize:12,cursor:"pointer",
                        boxShadow:`0 4px 12px ${color}40`,
                      }} className="btn-hover">설정</button>
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>{
                        if(!isLoggedIn){ setLoginTarget("heart"); setLoginModal(true); return; }
                      }} style={{
                        flex:1,padding:"13px",background:"#f5f0ff",color:"#a855f7",
                        border:"none",borderRadius:14,fontWeight:700,fontSize:13,cursor:"pointer",
                        display:"flex",alignItems:"center",justifyContent:"center",gap:6,
                      }} className="btn-hover"><Heart size={14}/> 관심</button>
                      <button onClick={()=>{
                        if(!isLoggedIn){ setLoginTarget("target"); setLoginModal(true); return; }
                      }} style={{
                        flex:2,padding:"13px",
                        background:"linear-gradient(135deg,#a855f7,#ec4899)",
                        color:"#fff",border:"none",borderRadius:14,fontWeight:800,fontSize:13,cursor:"pointer",
                        boxShadow:"0 4px 16px rgba(168,85,247,0.35)",
                        display:"flex",alignItems:"center",justifyContent:"center",gap:6,
                      }} className="btn-hover"><BellRing size={14}/> 최저가 알림받기</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

      </main>


      {/* ════ 상품 등록 모달 ════ */}
      {addModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(15,10,30,0.5)",backdropFilter:"blur(8px)",zIndex:999,display:"flex",alignItems:"flex-end",justifyContent:"center"}}
          onClick={()=>setAddModal(false)}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:"28px 28px 0 0",padding:"28px 20px 48px",width:"100%",maxWidth:600,boxShadow:"0 -8px 48px rgba(168,85,247,0.2)"}}>
            <div style={{width:36,height:4,background:"#e4d9f5",borderRadius:10,margin:"0 auto 22px"}}/>
            <div style={{fontWeight:900,fontSize:18,marginBottom:4}}>🧚 상품 등록</div>
            <div style={{fontSize:12,color:"#94a3b8",marginBottom:20}}>요정이 가격 내려가면 바로 알려드려요!</div>
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:700,color:"#374151",marginBottom:8}}>카테고리 선택</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {CATEGORIES.slice(1).map(c=>(
                  <button key={c.key} onClick={()=>setActiveCat(c.key)} style={{
                    padding:"6px 12px",borderRadius:20,fontSize:11,fontWeight:700,cursor:"pointer",
                    background:activeCat===c.key?catColor(c.key):"#f8f7ff",
                    color:activeCat===c.key?"#fff":"#94a3b8",border:"none",transition:"all 0.15s",
                  }}>{c.icon} {c.label}</button>
                ))}
              </div>
            </div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:700,color:"#374151",marginBottom:8}}>상품 URL</div>
              <input placeholder="https://www.coupang.com/..." style={{width:"100%",padding:"12px 14px",borderRadius:14,border:"1.5px solid rgba(192,132,252,0.2)",fontSize:12,outline:"none",boxSizing:"border-box",background:"#faf8ff"}}/>
            </div>
            <div style={{marginBottom:20}}>
              <div style={{fontSize:11,fontWeight:700,color:"#374151",marginBottom:8}}>목표 가격 (원)</div>
              <input placeholder="예: 32000" type="number" style={{width:"100%",padding:"12px 14px",borderRadius:14,border:"1.5px solid rgba(192,132,252,0.2)",fontSize:12,outline:"none",boxSizing:"border-box",background:"#faf8ff"}}/>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setAddModal(false)} style={{flex:1,padding:"13px",border:"1.5px solid rgba(192,132,252,0.2)",borderRadius:14,background:"#fff",color:"#94a3b8",fontWeight:700,fontSize:13,cursor:"pointer"}}>취소</button>
              <button onClick={()=>setAddModal(false)} style={{flex:2,padding:"13px",background:"linear-gradient(135deg,#a855f7,#ec4899)",color:"#fff",border:"none",borderRadius:14,fontWeight:800,fontSize:13,cursor:"pointer",boxShadow:"0 4px 16px rgba(168,85,247,0.35)"}} className="btn-hover">등록하기 <Bell size={13}/></button>
            </div>
          </div>
        </div>
      )}

      {/* ════ 최저가 알림받기 (카카오 로그인) 모달 ════ */}
      {loginModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(15,10,30,0.6)",backdropFilter:"blur(8px)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
          onClick={()=>setLoginModal(false)}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:28,padding:28,width:"100%",maxWidth:360,boxShadow:"0 16px 56px rgba(0,0,0,0.2)"}}>
            <div style={{textAlign:"center",marginBottom:24}}>
              <div style={{width:64,height:64,borderRadius:20,background:"linear-gradient(135deg,#c084fc,#f472b6)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px",boxShadow:"0 6px 20px rgba(192,132,252,0.4)"}}>
                <BellRing size={32} color="#fff"/>
              </div>
              <div style={{fontWeight:900,fontSize:18,marginBottom:6}}>최저가 알림받기</div>
              <div style={{fontSize:12,color:"#64748b",lineHeight:1.6}}>
                {loginTarget==="target" ? "이 상품 최저가 달성 시 카톡으로 알림받아요" : "관심 상품 저장하고 최저가 알림받아요"}
              </div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20}}>
              {[
                [<Bell size={16} color="#a855f7"/>,"목표가 달성 시 카카오톡 즉시 알림"],
                [<Heart size={16} color="#ec4899"/>,"관심 상품 저장 및 관리"],
                [<BarChart2 size={16} color="#f59e0b"/>,"내 절약 현황 한눈에 확인"],
                [<BellRing size={16} color="#10b981"/>,"타임세일 긴급 알림"],
              ].map(([icon,text])=>(
                <div key={text} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"#faf8ff",borderRadius:12,fontSize:12,fontWeight:600,color:"#374151"}}>
                  {icon} {text}
                </div>
              ))}
            </div>
            <button onClick={handleKakaoLogin} style={{
              width:"100%",padding:"15px",background:"#FEE500",color:"#3A1D1D",
              border:"none",borderRadius:16,fontWeight:900,fontSize:15,cursor:"pointer",
              boxShadow:"0 6px 20px rgba(254,229,0,0.5)",marginBottom:8,
              display:"flex",alignItems:"center",justifyContent:"center",gap:8,
            }} className="btn-hover">
              <MessageCircle size={18}/>
              카카오로 알림받기
            </button>
            <button onClick={()=>setLoginModal(false)} style={{width:"100%",padding:"12px",background:"none",border:"none",color:"#94a3b8",fontSize:12,cursor:"pointer",fontWeight:600}}>나중에 할게요</button>
          </div>
        </div>
      )}

      {/* ════ 카카오 채널 모달 ════ */}
      {kakaoModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(15,10,30,0.5)",backdropFilter:"blur(8px)",zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
          onClick={()=>setKakaoModal(false)}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:28,padding:28,width:"100%",maxWidth:360,boxShadow:"0 16px 56px rgba(0,0,0,0.2)"}}>
            <div style={{textAlign:"center",marginBottom:20}}>
              <div style={{width:64,height:64,borderRadius:20,background:"#FEE500",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px",boxShadow:"0 6px 20px rgba(254,229,0,0.5)"}}>
                <MessageCircle size={32} color="#3A1D1D"/>
              </div>
              <div style={{fontWeight:900,fontSize:18,marginBottom:4}}>절약요정 카카오채널</div>
              <div style={{fontSize:12,color:"#64748b",lineHeight:1.6}}>구독하면 가격 알림을<br/>카톡으로 바로 받아요!</div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20}}>
              {[
                [<Bell size={16} color="#a855f7"/>,"목표가 도달 즉시 알림"],
                [<BarChart2 size={16} color="#f59e0b"/>,"매일 오전 8시 특가 소식"],
                [<TrendingDown size={16} color="#10b981"/>,"주간 절약 리포트"],
                [<BellRing size={16} color="#ef4444"/>,"타임세일 긴급 알림"],
              ].map(([icon,text])=>(
                <div key={text} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"#faf8ff",borderRadius:12,fontSize:12,fontWeight:600,color:"#374151"}}>
                  {icon} {text}
                </div>
              ))}
            </div>
            <button style={{
              width:"100%",padding:"15px",background:"#FEE500",color:"#3A1D1D",
              border:"none",borderRadius:16,fontWeight:900,fontSize:15,cursor:"pointer",
              boxShadow:"0 6px 20px rgba(254,229,0,0.5)",marginBottom:8,
              display:"flex",alignItems:"center",justifyContent:"center",gap:8,
            }} className="btn-hover">
              <MessageCircle size={18} color="#3A1D1D"/>
              카카오채널 구독하기 →
            </button>
            <button onClick={()=>setKakaoModal(false)} style={{width:"100%",padding:"12px",background:"none",border:"none",color:"#94a3b8",fontSize:12,cursor:"pointer",fontWeight:600}}>나중에 할게요</button>
          </div>
        </div>
      )}
    </div>
  );
}
